import type { LucideIcon } from 'lucide-react'
import {
  ArrowRightLeftIcon,
  BadgeCheckIcon,
  ClipboardListIcon,
  ContactIcon,
  HashIcon,
  HistoryIcon,
  LayersIcon,
  LifeBuoyIcon,
  LockIcon,
  MailIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  MonitorIcon,
  PhoneIcon,
  ServerIcon,
  SparklesIcon,
  ZapIcon,
  GlobeIcon,
  LinkIcon,
  MapPinIcon,
  WrenchIcon,
  CloudRainIcon,
  PizzaIcon,
  DropletsIcon,
  ShieldIcon,
  DumbbellIcon,
  StethoscopeIcon,
  Building2Icon,
  MegaphoneIcon,
  PhoneCallIcon,
  StarIcon,
  SmartphoneIcon,
} from 'lucide-react'

export const demoPhone = {
  display: '(725) 715-5070',
  tel: 'tel:+17257155070',
  sms: 'sms:+17257155070',
}

export const demoSiteUrl = 'https://tacotruck.adroimarketing.com'
export const threeDayLaunchUrl = 'https://3daywebsite.adroimarketing.com/'

export type Comparison = { old: string; living: string }

export const comparisons: Comparison[] = [
  { old: 'Email your web person and wait days', living: 'Say it once, live in minutes' },
  { old: 'Pay by the hour for small edits', living: 'Changes included in your plan every month' },
  { old: '“Did they make the change yet?”', living: 'We check your live site and confirm it’s there' },
  { old: 'Send 12 changes, get 10 done, start over', living: 'Every change tracked until it’s live' },
]

export type Step = { title: string; body: string; example?: string }

export const steps: Step[] = [
  { title: 'Say what you want.', body: 'Plain English works.', example: '“Change our Friday hours to 11 to 9.”' },
  { title: 'We build it.', body: 'Your change is made and published to your real website.' },
  {
    title: 'We prove it’s live.',
    body: 'We check your live website and only say “Done” when the change is really there.',
  },
]

export type Channel = {
  title: string
  body: string
  example?: string
  icon: LucideIcon
  action?: { label: string; href: string }
}

export const channels: Channel[] = [
  {
    title: 'Call it in',
    body: 'Call (725) 715-5070. Ava, our web assistant, asks which website and your 4-digit PIN, reads your change back, and sends it.',
    icon: PhoneIcon,
    action: { label: 'Call (725) 715-5070', href: demoPhone.tel },
  },
  {
    title: 'Text it',
    body: 'Text (725) 715-5070 from your phone on file. Run more than one website? We text back and ask which one.',
    example: '“PIN 1234 change our Friday hours to 11am to 9pm.”',
    icon: MessageSquareIcon,
    action: { label: 'Text (725) 715-5070', href: demoPhone.sms },
  },
  {
    title: 'Email it',
    body: 'Reply to your “Website change” email with what you want changed.',
    icon: MailIcon,
  },
  {
    title: 'Slack it',
    body: 'Send your change in Slack. If you have more than one website, tap a button to pick which one.',
    icon: HashIcon,
  },
  {
    title: 'Type it online',
    body: 'Open Living Websites inside your 1Dash.Pro, Powered by HighLevel account. Your live website sits right next to a chat box. Type it, press Send, and watch it update.',
    icon: MonitorIcon,
  },
]

export type WebsiteFeature = { title: string; body: string; icon: LucideIcon }

export const websiteFeatures: WebsiteFeature[] = [
  { title: 'Live in Minutes', body: 'Most changes are on your real website within minutes.', icon: ZapIcon },
  { title: 'Proof, Not Promises', body: 'We check your live site and confirm the change is there.', icon: BadgeCheckIcon },
  {
    title: 'Never Stuck',
    body: 'If a change is held up, our system catches it and finishes it, so you never chase it or send it twice.',
    icon: LifeBuoyIcon,
  },
  { title: 'All Your Websites in One Place', body: 'Every website you run, managed from one account.', icon: LayersIcon },
  {
    title: 'Build a Brand-New Website',
    body: 'Answer a few questions and your new site is built in minutes.',
    icon: SparklesIcon,
  },
  {
    title: 'Already Have a Website? We Bring It Over',
    body: 'We read your current site and rebuild it, then switch your web address over when you’re ready, so your site never goes down.',
    icon: ArrowRightLeftIcon,
  },
  {
    title: 'Ask for Anything',
    body: 'Headlines, hours, photos, new services, new pages, specials.',
    icon: MessageCircleIcon,
  },
  {
    title: 'Built Into Your CRM',
    body: 'Your website lives in the same 1Dash.Pro, Powered by HighLevel account as your contacts, calendar, texting and reviews.',
    icon: ContactIcon,
  },
  { title: 'Every Change on the Record', body: 'See who asked for each change, and when.', icon: ClipboardListIcon },
  { title: 'Protected by Your PIN', body: 'Only people with your 4-digit PIN can make changes.', icon: LockIcon },
  {
    title: 'Every Change Saved as a Version',
    body: 'If a change isn’t what you wanted, we put it back.',
    icon: HistoryIcon,
  },
  { title: 'Hosting Included', body: 'Your website is hosted for you. Nothing else to set up.', icon: ServerIcon },
]

export const seasonalBanners = [
  { month: 'Oct', text: 'Spooky Slice Night — every Friday in October', tone: 'bg-brand-black text-white' },
  { month: 'Nov', text: 'Thanksgiving Day: open 11 to 3', tone: 'bg-brand-light text-brand-black' },
  { month: 'Dec', text: 'Holiday party trays — order now', tone: 'bg-brand-red text-white' },
]

export const corePageTypes = ['Home', 'About', 'Services', 'Contact', 'Blog']

export const proofStats: { label: string; icon: LucideIcon }[] = [
  { label: 'Hundreds of sites built', icon: GlobeIcon },
  { label: 'Changes done in minutes', icon: ZapIcon },
  { label: 'Use and keep your own domain', icon: LinkIcon },
  { label: 'Real people in Las Vegas', icon: MapPinIcon },
]

export const includedGroups = [
  {
    title: 'Your website',
    items: [
      'A complete custom website',
      'Looks great on every device',
      'Advanced security',
      'Connect and keep your own domain',
      'Add unlimited pages',
      'Custom landing pages for your campaigns',
      'Daily backups',
      'Version control, roll back anytime',
    ],
  },
  {
    title: 'Everything in one place',
    items: [
      'Online booking calendar',
      'Contact forms, quizzes and surveys',
      'Website chat',
      'Missed-call text-back',
      'One inbox for texts, email, Facebook, Instagram, Google Business Profile and WhatsApp messages',
    ],
  },
]

export type RealSite = { name: string; industry: string; icon: LucideIcon }

export const realSites: RealSite[] = [
  { name: 'Done Right Plumbing', industry: 'Plumbing', icon: WrenchIcon },
  { name: 'A-Flow Seamless Rain Gutters', industry: 'Home Services', icon: CloudRainIcon },
  { name: 'Caballero’s Electric', industry: 'Electrical', icon: ZapIcon },
  { name: 'Pizza Warehouse', industry: 'Restaurant', icon: PizzaIcon },
  { name: 'SoftWash Pros', industry: 'Exterior Cleaning', icon: DropletsIcon },
  { name: 'Parkway Esthetics', industry: 'Med Spa & Esthetics', icon: SparklesIcon },
  { name: 'KRT Insurance', industry: 'Insurance', icon: ShieldIcon },
  { name: 'Iron Eight MT', industry: 'Fitness & Martial Arts', icon: DumbbellIcon },
  { name: 'Arissa Cost Solutions', industry: 'Healthcare', icon: StethoscopeIcon },
  { name: 'Cabo Deal Flow', industry: 'Real Estate', icon: Building2Icon },
  { name: 'Aesthetics Clinic', industry: 'Med Spa & Esthetics', icon: SparklesIcon },
  { name: 'Ad ROI Marketing', industry: 'Marketing Agency', icon: MegaphoneIcon },
]

export const fullSystem: { title: string; body: string; icon: LucideIcon }[] = [
  {
    title: 'Never miss another call',
    body: 'Phone answering 24/7 that books appointments on your calendar and texts you a summary after every call.',
    icon: PhoneCallIcon,
  },
  {
    title: 'Your reputation on autopilot',
    body: 'Review requests that keep new 5-star reviews coming in, and replies written in your voice.',
    icon: StarIcon,
  },
  {
    title: 'Run your business from your phone',
    body: 'A real business phone system plus a mobile app to manage leads, customers and your calendar from anywhere.',
    icon: SmartphoneIcon,
  },
]

export type StartPlan = { name: string; price: string; badge?: string; lead?: string; items: string[] }

export const startPlans: StartPlan[] = [
  {
    name: 'Launch',
    price: '$315',
    items: [
      'Your website built in 3 days',
      'Changes in minutes by phone, text, email, Slack or online',
      '25 website changes a month',
      '1Dash.Pro, Powered by HighLevel',
      'Booking calendar',
      'One inbox',
    ],
  },
  {
    name: 'Pilot',
    price: '$615',
    badge: 'Best value',
    lead: 'Everything in Launch, plus:',
    items: [
      '50 website changes a month',
      '24/7 phone answering',
      'Review automation',
      'Full CRM with pipelines and automation',
      'QuickBooks Online integration',
    ],
  },
]

export const buildSteps = [
  {
    title: 'You tell us about your business.',
    body: 'One short intake: what you do, who you serve, where your leads come from.',
  },
  {
    title: 'We build your site in 3 days.',
    body: 'Structure, pages and layout, live and viewable. Built for your business, not a template demo.',
  },
  {
    title: 'You review it and ask for changes.',
    body: 'By phone, text, email, Slack or online. Done in minutes, for as long as you are with us.',
  },
]

export const exploreLinks = [
  { label: '1Dash.Pro CRM', href: '#/crm' },
  { label: 'Plans & Pricing', href: '#/pricing' },
  { label: 'Additional Costs', href: '#/pricing/additional-costs' },
  { label: 'Industries We Serve', href: '#/industries' },
  { label: '3-Day Website Launch', href: threeDayLaunchUrl, external: true },
  { label: 'Contact Us', href: '#/contact' },
]

export const includedChangeNotes = [
  'Your changes are one shared total across all your websites.',
  'Each request counts as one change.',
  'Changes reset every month.',
]

export const comingSoon = [
  'Roll back any change yourself with one click',
  'Automatic search upkeep',
  'Home page A/B testing',
  'Competitor website monitoring',
  'Automatic form testing',
]
